import * as z from 'zod';

export const CreatePoolDefinitionRequest = z.object({
  poolDefinition: z
    .object({
      _id: z
        .string()
        .describe('Pool definition ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the pool definition is updated.\nTo prevent conflicting changes, the current revision must be specified when updating the pool definition.\n\nIgnored when creating a pool definition.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the pool definition was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the pool definition was updated.')
        .optional()
        .nullable(),
      displayName: z
        .string()
        .describe('Pool definition name.')
        .max(128)
        .optional(),
      programDefinitionIds: z.array(z.string()).max(20).optional(),
      details: z
        .object({
          benefits: z
            .array(
              z.object({
                benefitKey: z
                  .string()
                  .describe(
                    'Unique identifier for this benefit.\n\nThis key is consistent across the pool definition and all associated pools that contain this benefit.'
                  )
                  .max(64),
                itemSetId: z
                  .string()
                  .describe(
                    'ID that is used to associated items with this benefit.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                price: z
                  .string()
                  .describe(
                    "Price of the benefit in credits. The price is the same for all of this benefit's items."
                  )
                  .optional()
                  .nullable(),
                additionalData: z
                  .record(z.string(), z.any())
                  .describe('Additional information for this benefit.')
                  .optional()
                  .nullable(),
                providerAppId: z
                  .string()
                  .describe('ID of the app providing the benefit items.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
                displayName: z
                  .string()
                  .describe('Benefit name.')
                  .max(40)
                  .optional()
                  .nullable(),
                description: z
                  .string()
                  .describe('Benefit description.')
                  .max(255)
                  .optional()
                  .nullable(),
              })
            )
            .max(10),
          creditConfiguration: z
            .object({
              amount: z
                .string()
                .describe('Initial available amount for associated balances.')
                .optional(),
              rolloverConfiguration: z
                .object({
                  enabled: z
                    .boolean()
                    .describe(
                      'Whether unused credits roll over to a new cycle when a program renews.'
                    )
                    .optional()
                    .nullable(),
                  balanceCap: z
                    .string()
                    .describe(
                      'Maximum number of credits that can roll over to the next cycle when a program renews.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Rollover settings.')
                .optional(),
              unitDisplayName: z
                .string()
                .describe('Credit unit display name.')
                .max(32)
                .optional()
                .nullable(),
            })
            .describe(
              "Credit settings.\n\nIf this object is empty, you can't set a price for the benefit."
            )
            .optional(),
          additionalData: z
            .record(z.string(), z.any())
            .describe('Additional information relating to this object.')
            .optional()
            .nullable(),
        })
        .describe('Pool definition benefits and settings.'),
      namespace: z
        .string()
        .describe(
          "Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created."
        )
        .min(1)
        .max(20),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the pool definition object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
      programDefinitions: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('Program definition ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            externalId: z
              .string()
              .describe('Program definition external ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
          })
        )
        .max(20)
        .optional(),
    })
    .describe('Pool definition to create.'),
  options: z
    .object({
      cascade: z
        .enum([
          'UNKNOWN_CASCADE',
          'IMMEDIATELY',
          'NEXT_RENEWAL',
          'FUTURE_PROVISIONS',
        ])
        .optional(),
    })
    .optional(),
});
export const CreatePoolDefinitionResponse = z.object({
  _id: z
    .string()
    .describe('Pool definition ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the pool definition is updated.\nTo prevent conflicting changes, the current revision must be specified when updating the pool definition.\n\nIgnored when creating a pool definition.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the pool definition was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the pool definition was updated.')
    .optional()
    .nullable(),
  displayName: z.string().describe('Pool definition name.').max(128).optional(),
  programDefinitionIds: z.array(z.string()).max(20).optional(),
  details: z
    .object({
      benefits: z
        .array(
          z.object({
            benefitKey: z
              .string()
              .describe(
                'Unique identifier for this benefit.\n\nThis key is consistent across the pool definition and all associated pools that contain this benefit.'
              )
              .max(64)
              .optional(),
            itemSetId: z
              .string()
              .describe(
                'ID that is used to associated items with this benefit.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            price: z
              .string()
              .describe(
                "Price of the benefit in credits. The price is the same for all of this benefit's items."
              )
              .optional()
              .nullable(),
            additionalData: z
              .record(z.string(), z.any())
              .describe('Additional information for this benefit.')
              .optional()
              .nullable(),
            providerAppId: z
              .string()
              .describe('ID of the app providing the benefit items.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            displayName: z
              .string()
              .describe('Benefit name.')
              .max(40)
              .optional()
              .nullable(),
            description: z
              .string()
              .describe('Benefit description.')
              .max(255)
              .optional()
              .nullable(),
          })
        )
        .max(10)
        .optional(),
      creditConfiguration: z
        .object({
          amount: z
            .string()
            .describe('Initial available amount for associated balances.')
            .optional(),
          rolloverConfiguration: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether unused credits roll over to a new cycle when a program renews.'
                )
                .optional()
                .nullable(),
              balanceCap: z
                .string()
                .describe(
                  'Maximum number of credits that can roll over to the next cycle when a program renews.'
                )
                .optional()
                .nullable(),
            })
            .describe('Rollover settings.')
            .optional(),
          unitDisplayName: z
            .string()
            .describe('Credit unit display name.')
            .max(32)
            .optional()
            .nullable(),
        })
        .describe(
          "Credit settings.\n\nIf this object is empty, you can't set a price for the benefit."
        )
        .optional(),
      additionalData: z
        .record(z.string(), z.any())
        .describe('Additional information relating to this object.')
        .optional()
        .nullable(),
    })
    .describe('Pool definition benefits and settings.')
    .optional(),
  namespace: z
    .string()
    .describe(
      "Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created."
    )
    .min(1)
    .max(20)
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Custom field data for the pool definition object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
    )
    .optional(),
  programDefinitions: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Program definition ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        externalId: z
          .string()
          .describe('Program definition external ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
      })
    )
    .max(20)
    .optional(),
});
export const BulkCreatePoolDefinitionsRequest = z.object({
  poolDefinitions: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Pool definition ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the pool definition is updated.\nTo prevent conflicting changes, the current revision must be specified when updating the pool definition.\n\nIgnored when creating a pool definition.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the pool definition was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the pool definition was updated.')
          .optional()
          .nullable(),
        displayName: z
          .string()
          .describe('Pool definition name.')
          .max(128)
          .optional(),
        programDefinitionIds: z.array(z.string()).max(20).optional(),
        details: z
          .object({
            benefits: z
              .array(
                z.object({
                  benefitKey: z
                    .string()
                    .describe(
                      'Unique identifier for this benefit.\n\nThis key is consistent across the pool definition and all associated pools that contain this benefit.'
                    )
                    .max(64),
                  itemSetId: z
                    .string()
                    .describe(
                      'ID that is used to associated items with this benefit.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  price: z
                    .string()
                    .describe(
                      "Price of the benefit in credits. The price is the same for all of this benefit's items."
                    )
                    .optional()
                    .nullable(),
                  additionalData: z
                    .record(z.string(), z.any())
                    .describe('Additional information for this benefit.')
                    .optional()
                    .nullable(),
                  providerAppId: z
                    .string()
                    .describe('ID of the app providing the benefit items.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    ),
                  displayName: z
                    .string()
                    .describe('Benefit name.')
                    .max(40)
                    .optional()
                    .nullable(),
                  description: z
                    .string()
                    .describe('Benefit description.')
                    .max(255)
                    .optional()
                    .nullable(),
                })
              )
              .max(10),
            creditConfiguration: z
              .object({
                amount: z
                  .string()
                  .describe('Initial available amount for associated balances.')
                  .optional(),
                rolloverConfiguration: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether unused credits roll over to a new cycle when a program renews.'
                      )
                      .optional()
                      .nullable(),
                    balanceCap: z
                      .string()
                      .describe(
                        'Maximum number of credits that can roll over to the next cycle when a program renews.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Rollover settings.')
                  .optional(),
                unitDisplayName: z
                  .string()
                  .describe('Credit unit display name.')
                  .max(32)
                  .optional()
                  .nullable(),
              })
              .describe(
                "Credit settings.\n\nIf this object is empty, you can't set a price for the benefit."
              )
              .optional(),
            additionalData: z
              .record(z.string(), z.any())
              .describe('Additional information relating to this object.')
              .optional()
              .nullable(),
          })
          .describe('Pool definition benefits and settings.'),
        namespace: z
          .string()
          .describe(
            "Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created."
          )
          .min(1)
          .max(20),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for the pool definition object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        programDefinitions: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Program definition ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              externalId: z
                .string()
                .describe('Program definition external ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            })
          )
          .max(20)
          .optional(),
      })
    )
    .min(1)
    .max(100),
  options: z
    .object({
      cascade: z
        .enum([
          'UNKNOWN_CASCADE',
          'IMMEDIATELY',
          'NEXT_RENEWAL',
          'FUTURE_PROVISIONS',
        ])
        .optional(),
      returnEntity: z
        .boolean()
        .describe(
          'Whether to return the created pool definitions.\n\nDefault: `false`'
        )
        .optional(),
    })
    .optional(),
});
export const BulkCreatePoolDefinitionsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Item metadata.')
          .optional(),
        item: z
          .object({
            _id: z
              .string()
              .describe('Pool definition ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the pool definition is updated.\nTo prevent conflicting changes, the current revision must be specified when updating the pool definition.\n\nIgnored when creating a pool definition.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe('Date and time the pool definition was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the pool definition was updated.')
              .optional()
              .nullable(),
            displayName: z
              .string()
              .describe('Pool definition name.')
              .max(128)
              .optional(),
            programDefinitionIds: z.array(z.string()).max(20).optional(),
            details: z
              .object({
                benefits: z
                  .array(
                    z.object({
                      benefitKey: z
                        .string()
                        .describe(
                          'Unique identifier for this benefit.\n\nThis key is consistent across the pool definition and all associated pools that contain this benefit.'
                        )
                        .max(64)
                        .optional(),
                      itemSetId: z
                        .string()
                        .describe(
                          'ID that is used to associated items with this benefit.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      price: z
                        .string()
                        .describe(
                          "Price of the benefit in credits. The price is the same for all of this benefit's items."
                        )
                        .optional()
                        .nullable(),
                      additionalData: z
                        .record(z.string(), z.any())
                        .describe('Additional information for this benefit.')
                        .optional()
                        .nullable(),
                      providerAppId: z
                        .string()
                        .describe('ID of the app providing the benefit items.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      displayName: z
                        .string()
                        .describe('Benefit name.')
                        .max(40)
                        .optional()
                        .nullable(),
                      description: z
                        .string()
                        .describe('Benefit description.')
                        .max(255)
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(10)
                  .optional(),
                creditConfiguration: z
                  .object({
                    amount: z
                      .string()
                      .describe(
                        'Initial available amount for associated balances.'
                      )
                      .optional(),
                    rolloverConfiguration: z
                      .object({
                        enabled: z
                          .boolean()
                          .describe(
                            'Whether unused credits roll over to a new cycle when a program renews.'
                          )
                          .optional()
                          .nullable(),
                        balanceCap: z
                          .string()
                          .describe(
                            'Maximum number of credits that can roll over to the next cycle when a program renews.'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe('Rollover settings.')
                      .optional(),
                    unitDisplayName: z
                      .string()
                      .describe('Credit unit display name.')
                      .max(32)
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    "Credit settings.\n\nIf this object is empty, you can't set a price for the benefit."
                  )
                  .optional(),
                additionalData: z
                  .record(z.string(), z.any())
                  .describe('Additional information relating to this object.')
                  .optional()
                  .nullable(),
              })
              .describe('Pool definition benefits and settings.')
              .optional(),
            namespace: z
              .string()
              .describe(
                "Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created."
              )
              .min(1)
              .max(20)
              .optional()
              .nullable(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe(
                'Custom field data for the pool definition object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
              )
              .optional(),
            programDefinitions: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe('Program definition ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  externalId: z
                    .string()
                    .describe('Program definition external ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                })
              )
              .max(20)
              .optional(),
          })
          .describe('Pool definition.')
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk action metadata.')
    .optional(),
});
export const UpdatePoolDefinitionRequest = z.object({
  _id: z
    .string()
    .describe('Pool definition ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  poolDefinition: z
    .object({
      _id: z
        .string()
        .describe('Pool definition ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the pool definition is updated.\nTo prevent conflicting changes, the current revision must be specified when updating the pool definition.\n\nIgnored when creating a pool definition.'
        ),
      _createdDate: z
        .date()
        .describe('Date and time the pool definition was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the pool definition was updated.')
        .optional()
        .nullable(),
      displayName: z
        .string()
        .describe('Pool definition name.')
        .max(128)
        .optional(),
      programDefinitionIds: z.array(z.string()).max(20).optional(),
      details: z
        .object({
          benefits: z
            .array(
              z.object({
                benefitKey: z
                  .string()
                  .describe(
                    'Unique identifier for this benefit.\n\nThis key is consistent across the pool definition and all associated pools that contain this benefit.'
                  )
                  .max(64)
                  .optional(),
                itemSetId: z
                  .string()
                  .describe(
                    'ID that is used to associated items with this benefit.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                price: z
                  .string()
                  .describe(
                    "Price of the benefit in credits. The price is the same for all of this benefit's items."
                  )
                  .optional()
                  .nullable(),
                additionalData: z
                  .record(z.string(), z.any())
                  .describe('Additional information for this benefit.')
                  .optional()
                  .nullable(),
                providerAppId: z
                  .string()
                  .describe('ID of the app providing the benefit items.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                displayName: z
                  .string()
                  .describe('Benefit name.')
                  .max(40)
                  .optional()
                  .nullable(),
                description: z
                  .string()
                  .describe('Benefit description.')
                  .max(255)
                  .optional()
                  .nullable(),
              })
            )
            .max(10)
            .optional(),
          creditConfiguration: z
            .object({
              amount: z
                .string()
                .describe('Initial available amount for associated balances.')
                .optional(),
              rolloverConfiguration: z
                .object({
                  enabled: z
                    .boolean()
                    .describe(
                      'Whether unused credits roll over to a new cycle when a program renews.'
                    )
                    .optional()
                    .nullable(),
                  balanceCap: z
                    .string()
                    .describe(
                      'Maximum number of credits that can roll over to the next cycle when a program renews.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Rollover settings.')
                .optional(),
              unitDisplayName: z
                .string()
                .describe('Credit unit display name.')
                .max(32)
                .optional()
                .nullable(),
            })
            .describe(
              "Credit settings.\n\nIf this object is empty, you can't set a price for the benefit."
            )
            .optional(),
          additionalData: z
            .record(z.string(), z.any())
            .describe('Additional information relating to this object.')
            .optional()
            .nullable(),
        })
        .describe('Pool definition benefits and settings.')
        .optional(),
      namespace: z
        .string()
        .describe(
          "Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created."
        )
        .min(1)
        .max(20)
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the pool definition object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
      programDefinitions: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('Program definition ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            externalId: z
              .string()
              .describe('Program definition external ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
          })
        )
        .max(20)
        .optional(),
    })
    .describe('Pool definition to update.'),
  options: z
    .object({
      cascade: z
        .enum([
          'UNKNOWN_CASCADE',
          'IMMEDIATELY',
          'NEXT_RENEWAL',
          'FUTURE_PROVISIONS',
        ])
        .optional(),
    })
    .optional(),
});
export const UpdatePoolDefinitionResponse = z.object({
  _id: z
    .string()
    .describe('Pool definition ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the pool definition is updated.\nTo prevent conflicting changes, the current revision must be specified when updating the pool definition.\n\nIgnored when creating a pool definition.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the pool definition was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the pool definition was updated.')
    .optional()
    .nullable(),
  displayName: z.string().describe('Pool definition name.').max(128).optional(),
  programDefinitionIds: z.array(z.string()).max(20).optional(),
  details: z
    .object({
      benefits: z
        .array(
          z.object({
            benefitKey: z
              .string()
              .describe(
                'Unique identifier for this benefit.\n\nThis key is consistent across the pool definition and all associated pools that contain this benefit.'
              )
              .max(64)
              .optional(),
            itemSetId: z
              .string()
              .describe(
                'ID that is used to associated items with this benefit.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            price: z
              .string()
              .describe(
                "Price of the benefit in credits. The price is the same for all of this benefit's items."
              )
              .optional()
              .nullable(),
            additionalData: z
              .record(z.string(), z.any())
              .describe('Additional information for this benefit.')
              .optional()
              .nullable(),
            providerAppId: z
              .string()
              .describe('ID of the app providing the benefit items.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            displayName: z
              .string()
              .describe('Benefit name.')
              .max(40)
              .optional()
              .nullable(),
            description: z
              .string()
              .describe('Benefit description.')
              .max(255)
              .optional()
              .nullable(),
          })
        )
        .max(10)
        .optional(),
      creditConfiguration: z
        .object({
          amount: z
            .string()
            .describe('Initial available amount for associated balances.')
            .optional(),
          rolloverConfiguration: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether unused credits roll over to a new cycle when a program renews.'
                )
                .optional()
                .nullable(),
              balanceCap: z
                .string()
                .describe(
                  'Maximum number of credits that can roll over to the next cycle when a program renews.'
                )
                .optional()
                .nullable(),
            })
            .describe('Rollover settings.')
            .optional(),
          unitDisplayName: z
            .string()
            .describe('Credit unit display name.')
            .max(32)
            .optional()
            .nullable(),
        })
        .describe(
          "Credit settings.\n\nIf this object is empty, you can't set a price for the benefit."
        )
        .optional(),
      additionalData: z
        .record(z.string(), z.any())
        .describe('Additional information relating to this object.')
        .optional()
        .nullable(),
    })
    .describe('Pool definition benefits and settings.')
    .optional(),
  namespace: z
    .string()
    .describe(
      "Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created."
    )
    .min(1)
    .max(20)
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Custom field data for the pool definition object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
    )
    .optional(),
  programDefinitions: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Program definition ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        externalId: z
          .string()
          .describe('Program definition external ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
      })
    )
    .max(20)
    .optional(),
});
export const BulkUpdatePoolDefinitionsRequest = z.object({
  options: z
    .object({
      poolDefinitions: z
        .array(
          z.object({
            poolDefinition: z
              .object({
                _id: z
                  .string()
                  .describe('Pool definition ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
                revision: z
                  .string()
                  .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                  .describe(
                    'Revision number, which increments by 1 each time the pool definition is updated.\nTo prevent conflicting changes, the current revision must be specified when updating the pool definition.\n\nIgnored when creating a pool definition.'
                  ),
                _createdDate: z
                  .date()
                  .describe('Date and time the pool definition was created.')
                  .optional()
                  .nullable(),
                _updatedDate: z
                  .date()
                  .describe('Date and time the pool definition was updated.')
                  .optional()
                  .nullable(),
                displayName: z
                  .string()
                  .describe('Pool definition name.')
                  .max(128)
                  .optional(),
                programDefinitionIds: z.array(z.string()).max(20).optional(),
                details: z
                  .object({
                    benefits: z
                      .array(
                        z.object({
                          benefitKey: z
                            .string()
                            .describe(
                              'Unique identifier for this benefit.\n\nThis key is consistent across the pool definition and all associated pools that contain this benefit.'
                            )
                            .max(64)
                            .optional(),
                          itemSetId: z
                            .string()
                            .describe(
                              'ID that is used to associated items with this benefit.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          price: z
                            .string()
                            .describe(
                              "Price of the benefit in credits. The price is the same for all of this benefit's items."
                            )
                            .optional()
                            .nullable(),
                          additionalData: z
                            .record(z.string(), z.any())
                            .describe(
                              'Additional information for this benefit.'
                            )
                            .optional()
                            .nullable(),
                          providerAppId: z
                            .string()
                            .describe(
                              'ID of the app providing the benefit items.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          displayName: z
                            .string()
                            .describe('Benefit name.')
                            .max(40)
                            .optional()
                            .nullable(),
                          description: z
                            .string()
                            .describe('Benefit description.')
                            .max(255)
                            .optional()
                            .nullable(),
                        })
                      )
                      .max(10)
                      .optional(),
                    creditConfiguration: z
                      .object({
                        amount: z
                          .string()
                          .describe(
                            'Initial available amount for associated balances.'
                          )
                          .optional(),
                        rolloverConfiguration: z
                          .object({
                            enabled: z
                              .boolean()
                              .describe(
                                'Whether unused credits roll over to a new cycle when a program renews.'
                              )
                              .optional()
                              .nullable(),
                            balanceCap: z
                              .string()
                              .describe(
                                'Maximum number of credits that can roll over to the next cycle when a program renews.'
                              )
                              .optional()
                              .nullable(),
                          })
                          .describe('Rollover settings.')
                          .optional(),
                        unitDisplayName: z
                          .string()
                          .describe('Credit unit display name.')
                          .max(32)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        "Credit settings.\n\nIf this object is empty, you can't set a price for the benefit."
                      )
                      .optional(),
                    additionalData: z
                      .record(z.string(), z.any())
                      .describe(
                        'Additional information relating to this object.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Pool definition benefits and settings.')
                  .optional(),
                namespace: z
                  .string()
                  .describe(
                    "Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created."
                  )
                  .min(1)
                  .max(20)
                  .optional()
                  .nullable(),
                extendedFields: z
                  .object({
                    namespaces: z
                      .record(z.string(), z.record(z.string(), z.any()))
                      .describe(
                        'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                      )
                      .optional(),
                  })
                  .describe(
                    'Custom field data for the pool definition object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
                  )
                  .optional(),
                programDefinitions: z
                  .array(
                    z.object({
                      _id: z
                        .string()
                        .describe('Program definition ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                      externalId: z
                        .string()
                        .describe('Program definition external ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(20)
                  .optional(),
              })
              .describe('Pool definition to update.'),
            fieldMask: z.array(z.string()).optional(),
          })
        )
        .min(1)
        .max(100)
        .optional(),
      cascade: z
        .enum([
          'UNKNOWN_CASCADE',
          'IMMEDIATELY',
          'NEXT_RENEWAL',
          'FUTURE_PROVISIONS',
        ])
        .optional(),
      returnEntity: z
        .boolean()
        .describe(
          'Whether to return the updated pool definitions.\n\nDefault: `false`'
        )
        .optional(),
    })
    .optional(),
});
export const BulkUpdatePoolDefinitionsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Item metadata.')
          .optional(),
        item: z
          .object({
            _id: z
              .string()
              .describe('Pool definition ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the pool definition is updated.\nTo prevent conflicting changes, the current revision must be specified when updating the pool definition.\n\nIgnored when creating a pool definition.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe('Date and time the pool definition was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the pool definition was updated.')
              .optional()
              .nullable(),
            displayName: z
              .string()
              .describe('Pool definition name.')
              .max(128)
              .optional(),
            programDefinitionIds: z.array(z.string()).max(20).optional(),
            details: z
              .object({
                benefits: z
                  .array(
                    z.object({
                      benefitKey: z
                        .string()
                        .describe(
                          'Unique identifier for this benefit.\n\nThis key is consistent across the pool definition and all associated pools that contain this benefit.'
                        )
                        .max(64)
                        .optional(),
                      itemSetId: z
                        .string()
                        .describe(
                          'ID that is used to associated items with this benefit.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      price: z
                        .string()
                        .describe(
                          "Price of the benefit in credits. The price is the same for all of this benefit's items."
                        )
                        .optional()
                        .nullable(),
                      additionalData: z
                        .record(z.string(), z.any())
                        .describe('Additional information for this benefit.')
                        .optional()
                        .nullable(),
                      providerAppId: z
                        .string()
                        .describe('ID of the app providing the benefit items.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      displayName: z
                        .string()
                        .describe('Benefit name.')
                        .max(40)
                        .optional()
                        .nullable(),
                      description: z
                        .string()
                        .describe('Benefit description.')
                        .max(255)
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(10)
                  .optional(),
                creditConfiguration: z
                  .object({
                    amount: z
                      .string()
                      .describe(
                        'Initial available amount for associated balances.'
                      )
                      .optional(),
                    rolloverConfiguration: z
                      .object({
                        enabled: z
                          .boolean()
                          .describe(
                            'Whether unused credits roll over to a new cycle when a program renews.'
                          )
                          .optional()
                          .nullable(),
                        balanceCap: z
                          .string()
                          .describe(
                            'Maximum number of credits that can roll over to the next cycle when a program renews.'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe('Rollover settings.')
                      .optional(),
                    unitDisplayName: z
                      .string()
                      .describe('Credit unit display name.')
                      .max(32)
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    "Credit settings.\n\nIf this object is empty, you can't set a price for the benefit."
                  )
                  .optional(),
                additionalData: z
                  .record(z.string(), z.any())
                  .describe('Additional information relating to this object.')
                  .optional()
                  .nullable(),
              })
              .describe('Pool definition benefits and settings.')
              .optional(),
            namespace: z
              .string()
              .describe(
                "Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created."
              )
              .min(1)
              .max(20)
              .optional()
              .nullable(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe(
                'Custom field data for the pool definition object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
              )
              .optional(),
            programDefinitions: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe('Program definition ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  externalId: z
                    .string()
                    .describe('Program definition external ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                })
              )
              .max(20)
              .optional(),
          })
          .describe('Pool definition.')
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk action metadata.')
    .optional(),
});
export const DeletePoolDefinitionRequest = z.object({
  poolDefinitionId: z
    .string()
    .describe('ID of the pool definition to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  cascade: z.enum([
    'UNKNOWN_CASCADE',
    'IMMEDIATELY',
    'NEXT_RENEWAL',
    'FUTURE_PROVISIONS',
  ]),
});
export const DeletePoolDefinitionResponse = z.object({});
export const BulkDeletePoolDefinitionsRequest = z.object({
  poolDefinitionIds: z.array(z.string()).min(1).max(100),
  options: z
    .object({
      cascade: z
        .enum([
          'UNKNOWN_CASCADE',
          'IMMEDIATELY',
          'NEXT_RENEWAL',
          'FUTURE_PROVISIONS',
        ])
        .optional(),
    })
    .optional(),
});
export const BulkDeletePoolDefinitionsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Item metadata.')
          .optional(),
        item: z
          .object({
            _id: z
              .string()
              .describe('Pool definition ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the pool definition is updated.\nTo prevent conflicting changes, the current revision must be specified when updating the pool definition.\n\nIgnored when creating a pool definition.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe('Date and time the pool definition was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the pool definition was updated.')
              .optional()
              .nullable(),
            displayName: z
              .string()
              .describe('Pool definition name.')
              .max(128)
              .optional(),
            programDefinitionIds: z.array(z.string()).max(20).optional(),
            details: z
              .object({
                benefits: z
                  .array(
                    z.object({
                      benefitKey: z
                        .string()
                        .describe(
                          'Unique identifier for this benefit.\n\nThis key is consistent across the pool definition and all associated pools that contain this benefit.'
                        )
                        .max(64)
                        .optional(),
                      itemSetId: z
                        .string()
                        .describe(
                          'ID that is used to associated items with this benefit.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      price: z
                        .string()
                        .describe(
                          "Price of the benefit in credits. The price is the same for all of this benefit's items."
                        )
                        .optional()
                        .nullable(),
                      additionalData: z
                        .record(z.string(), z.any())
                        .describe('Additional information for this benefit.')
                        .optional()
                        .nullable(),
                      providerAppId: z
                        .string()
                        .describe('ID of the app providing the benefit items.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      displayName: z
                        .string()
                        .describe('Benefit name.')
                        .max(40)
                        .optional()
                        .nullable(),
                      description: z
                        .string()
                        .describe('Benefit description.')
                        .max(255)
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(10)
                  .optional(),
                creditConfiguration: z
                  .object({
                    amount: z
                      .string()
                      .describe(
                        'Initial available amount for associated balances.'
                      )
                      .optional(),
                    rolloverConfiguration: z
                      .object({
                        enabled: z
                          .boolean()
                          .describe(
                            'Whether unused credits roll over to a new cycle when a program renews.'
                          )
                          .optional()
                          .nullable(),
                        balanceCap: z
                          .string()
                          .describe(
                            'Maximum number of credits that can roll over to the next cycle when a program renews.'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe('Rollover settings.')
                      .optional(),
                    unitDisplayName: z
                      .string()
                      .describe('Credit unit display name.')
                      .max(32)
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    "Credit settings.\n\nIf this object is empty, you can't set a price for the benefit."
                  )
                  .optional(),
                additionalData: z
                  .record(z.string(), z.any())
                  .describe('Additional information relating to this object.')
                  .optional()
                  .nullable(),
              })
              .describe('Pool definition benefits and settings.')
              .optional(),
            namespace: z
              .string()
              .describe(
                "Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created."
              )
              .min(1)
              .max(20)
              .optional()
              .nullable(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe(
                'Custom field data for the pool definition object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
              )
              .optional(),
            programDefinitions: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe('Program definition ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  externalId: z
                    .string()
                    .describe('Program definition external ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                })
              )
              .max(20)
              .optional(),
          })
          .describe('Pool definition.')
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk action metadata.')
    .optional(),
});
export const GetPoolDefinitionRequest = z.object({
  poolDefinitionId: z
    .string()
    .describe('ID of the pool definition to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetPoolDefinitionResponse = z.object({
  _id: z
    .string()
    .describe('Pool definition ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the pool definition is updated.\nTo prevent conflicting changes, the current revision must be specified when updating the pool definition.\n\nIgnored when creating a pool definition.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the pool definition was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the pool definition was updated.')
    .optional()
    .nullable(),
  displayName: z.string().describe('Pool definition name.').max(128).optional(),
  programDefinitionIds: z.array(z.string()).max(20).optional(),
  details: z
    .object({
      benefits: z
        .array(
          z.object({
            benefitKey: z
              .string()
              .describe(
                'Unique identifier for this benefit.\n\nThis key is consistent across the pool definition and all associated pools that contain this benefit.'
              )
              .max(64)
              .optional(),
            itemSetId: z
              .string()
              .describe(
                'ID that is used to associated items with this benefit.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            price: z
              .string()
              .describe(
                "Price of the benefit in credits. The price is the same for all of this benefit's items."
              )
              .optional()
              .nullable(),
            additionalData: z
              .record(z.string(), z.any())
              .describe('Additional information for this benefit.')
              .optional()
              .nullable(),
            providerAppId: z
              .string()
              .describe('ID of the app providing the benefit items.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            displayName: z
              .string()
              .describe('Benefit name.')
              .max(40)
              .optional()
              .nullable(),
            description: z
              .string()
              .describe('Benefit description.')
              .max(255)
              .optional()
              .nullable(),
          })
        )
        .max(10)
        .optional(),
      creditConfiguration: z
        .object({
          amount: z
            .string()
            .describe('Initial available amount for associated balances.')
            .optional(),
          rolloverConfiguration: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether unused credits roll over to a new cycle when a program renews.'
                )
                .optional()
                .nullable(),
              balanceCap: z
                .string()
                .describe(
                  'Maximum number of credits that can roll over to the next cycle when a program renews.'
                )
                .optional()
                .nullable(),
            })
            .describe('Rollover settings.')
            .optional(),
          unitDisplayName: z
            .string()
            .describe('Credit unit display name.')
            .max(32)
            .optional()
            .nullable(),
        })
        .describe(
          "Credit settings.\n\nIf this object is empty, you can't set a price for the benefit."
        )
        .optional(),
      additionalData: z
        .record(z.string(), z.any())
        .describe('Additional information relating to this object.')
        .optional()
        .nullable(),
    })
    .describe('Pool definition benefits and settings.')
    .optional(),
  namespace: z
    .string()
    .describe(
      "Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created."
    )
    .min(1)
    .max(20)
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Custom field data for the pool definition object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
    )
    .optional(),
  programDefinitions: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Program definition ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        externalId: z
          .string()
          .describe('Program definition external ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
      })
    )
    .max(20)
    .optional(),
});
export const QueryPoolDefinitionsRequest = z.object({
  query: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object.\nSee [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language)\nfor more information.'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .max(5)
          .optional(),
      }),
      z.xor([
        z.object({ cursorPaging: z.never().optional() }),
        z.object({
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.'
            ),
        }),
      ])
    )
    .describe('Filter, sort, and paging to apply to the query.'),
});
export const QueryPoolDefinitionsResponse = z.object({
  poolDefinitions: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Pool definition ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the pool definition is updated.\nTo prevent conflicting changes, the current revision must be specified when updating the pool definition.\n\nIgnored when creating a pool definition.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the pool definition was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the pool definition was updated.')
          .optional()
          .nullable(),
        displayName: z
          .string()
          .describe('Pool definition name.')
          .max(128)
          .optional(),
        programDefinitionIds: z.array(z.string()).max(20).optional(),
        details: z
          .object({
            benefits: z
              .array(
                z.object({
                  benefitKey: z
                    .string()
                    .describe(
                      'Unique identifier for this benefit.\n\nThis key is consistent across the pool definition and all associated pools that contain this benefit.'
                    )
                    .max(64)
                    .optional(),
                  itemSetId: z
                    .string()
                    .describe(
                      'ID that is used to associated items with this benefit.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  price: z
                    .string()
                    .describe(
                      "Price of the benefit in credits. The price is the same for all of this benefit's items."
                    )
                    .optional()
                    .nullable(),
                  additionalData: z
                    .record(z.string(), z.any())
                    .describe('Additional information for this benefit.')
                    .optional()
                    .nullable(),
                  providerAppId: z
                    .string()
                    .describe('ID of the app providing the benefit items.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  displayName: z
                    .string()
                    .describe('Benefit name.')
                    .max(40)
                    .optional()
                    .nullable(),
                  description: z
                    .string()
                    .describe('Benefit description.')
                    .max(255)
                    .optional()
                    .nullable(),
                })
              )
              .max(10)
              .optional(),
            creditConfiguration: z
              .object({
                amount: z
                  .string()
                  .describe('Initial available amount for associated balances.')
                  .optional(),
                rolloverConfiguration: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether unused credits roll over to a new cycle when a program renews.'
                      )
                      .optional()
                      .nullable(),
                    balanceCap: z
                      .string()
                      .describe(
                        'Maximum number of credits that can roll over to the next cycle when a program renews.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Rollover settings.')
                  .optional(),
                unitDisplayName: z
                  .string()
                  .describe('Credit unit display name.')
                  .max(32)
                  .optional()
                  .nullable(),
              })
              .describe(
                "Credit settings.\n\nIf this object is empty, you can't set a price for the benefit."
              )
              .optional(),
            additionalData: z
              .record(z.string(), z.any())
              .describe('Additional information relating to this object.')
              .optional()
              .nullable(),
          })
          .describe('Pool definition benefits and settings.')
          .optional(),
        namespace: z
          .string()
          .describe(
            "Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created."
          )
          .min(1)
          .max(20)
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for the pool definition object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        programDefinitions: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Program definition ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              externalId: z
                .string()
                .describe('Program definition external ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            })
          )
          .max(20)
          .optional(),
      })
    )
    .optional(),
  metadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Metadata for the paginated results.')
    .optional(),
});
export const AddPoolDefinitionToProgramDefinitionRequest = z.object({
  poolDefinitionId: z
    .string()
    .describe('ID of the pool definition to add to the program definition.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z.object({
    programDefinitionId: z
      .string()
      .describe('ID of the program definition to add the pool definition to.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
    cascade: z.enum([
      'UNKNOWN_CASCADE',
      'IMMEDIATELY',
      'NEXT_RENEWAL',
      'FUTURE_PROVISIONS',
    ]),
  }),
});
export const AddPoolDefinitionToProgramDefinitionResponse = z.object({
  poolDefinition: z
    .object({
      _id: z
        .string()
        .describe('Pool definition ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the pool definition is updated.\nTo prevent conflicting changes, the current revision must be specified when updating the pool definition.\n\nIgnored when creating a pool definition.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the pool definition was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the pool definition was updated.')
        .optional()
        .nullable(),
      displayName: z
        .string()
        .describe('Pool definition name.')
        .max(128)
        .optional(),
      programDefinitionIds: z.array(z.string()).max(20).optional(),
      details: z
        .object({
          benefits: z
            .array(
              z.object({
                benefitKey: z
                  .string()
                  .describe(
                    'Unique identifier for this benefit.\n\nThis key is consistent across the pool definition and all associated pools that contain this benefit.'
                  )
                  .max(64)
                  .optional(),
                itemSetId: z
                  .string()
                  .describe(
                    'ID that is used to associated items with this benefit.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                price: z
                  .string()
                  .describe(
                    "Price of the benefit in credits. The price is the same for all of this benefit's items."
                  )
                  .optional()
                  .nullable(),
                additionalData: z
                  .record(z.string(), z.any())
                  .describe('Additional information for this benefit.')
                  .optional()
                  .nullable(),
                providerAppId: z
                  .string()
                  .describe('ID of the app providing the benefit items.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                displayName: z
                  .string()
                  .describe('Benefit name.')
                  .max(40)
                  .optional()
                  .nullable(),
                description: z
                  .string()
                  .describe('Benefit description.')
                  .max(255)
                  .optional()
                  .nullable(),
              })
            )
            .max(10)
            .optional(),
          creditConfiguration: z
            .object({
              amount: z
                .string()
                .describe('Initial available amount for associated balances.')
                .optional(),
              rolloverConfiguration: z
                .object({
                  enabled: z
                    .boolean()
                    .describe(
                      'Whether unused credits roll over to a new cycle when a program renews.'
                    )
                    .optional()
                    .nullable(),
                  balanceCap: z
                    .string()
                    .describe(
                      'Maximum number of credits that can roll over to the next cycle when a program renews.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Rollover settings.')
                .optional(),
              unitDisplayName: z
                .string()
                .describe('Credit unit display name.')
                .max(32)
                .optional()
                .nullable(),
            })
            .describe(
              "Credit settings.\n\nIf this object is empty, you can't set a price for the benefit."
            )
            .optional(),
          additionalData: z
            .record(z.string(), z.any())
            .describe('Additional information relating to this object.')
            .optional()
            .nullable(),
        })
        .describe('Pool definition benefits and settings.')
        .optional(),
      namespace: z
        .string()
        .describe(
          "Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created."
        )
        .min(1)
        .max(20)
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the pool definition object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
      programDefinitions: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('Program definition ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            externalId: z
              .string()
              .describe('Program definition external ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
          })
        )
        .max(20)
        .optional(),
    })
    .describe('Updated pool definition.')
    .optional(),
});
export const RemovePoolDefinitionFromProgramDefinitionRequest = z.object({
  poolDefinitionId: z
    .string()
    .describe(
      'ID of the pool definition to remove from the program definition.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z.object({
    programDefinitionId: z
      .string()
      .describe(
        'ID of the program definition to remove the pool definition from.'
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
    cascade: z.enum([
      'UNKNOWN_CASCADE',
      'IMMEDIATELY',
      'NEXT_RENEWAL',
      'FUTURE_PROVISIONS',
    ]),
  }),
});
export const RemovePoolDefinitionFromProgramDefinitionResponse = z.object({
  poolDefinition: z
    .object({
      _id: z
        .string()
        .describe('Pool definition ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the pool definition is updated.\nTo prevent conflicting changes, the current revision must be specified when updating the pool definition.\n\nIgnored when creating a pool definition.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the pool definition was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the pool definition was updated.')
        .optional()
        .nullable(),
      displayName: z
        .string()
        .describe('Pool definition name.')
        .max(128)
        .optional(),
      programDefinitionIds: z.array(z.string()).max(20).optional(),
      details: z
        .object({
          benefits: z
            .array(
              z.object({
                benefitKey: z
                  .string()
                  .describe(
                    'Unique identifier for this benefit.\n\nThis key is consistent across the pool definition and all associated pools that contain this benefit.'
                  )
                  .max(64)
                  .optional(),
                itemSetId: z
                  .string()
                  .describe(
                    'ID that is used to associated items with this benefit.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                price: z
                  .string()
                  .describe(
                    "Price of the benefit in credits. The price is the same for all of this benefit's items."
                  )
                  .optional()
                  .nullable(),
                additionalData: z
                  .record(z.string(), z.any())
                  .describe('Additional information for this benefit.')
                  .optional()
                  .nullable(),
                providerAppId: z
                  .string()
                  .describe('ID of the app providing the benefit items.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                displayName: z
                  .string()
                  .describe('Benefit name.')
                  .max(40)
                  .optional()
                  .nullable(),
                description: z
                  .string()
                  .describe('Benefit description.')
                  .max(255)
                  .optional()
                  .nullable(),
              })
            )
            .max(10)
            .optional(),
          creditConfiguration: z
            .object({
              amount: z
                .string()
                .describe('Initial available amount for associated balances.')
                .optional(),
              rolloverConfiguration: z
                .object({
                  enabled: z
                    .boolean()
                    .describe(
                      'Whether unused credits roll over to a new cycle when a program renews.'
                    )
                    .optional()
                    .nullable(),
                  balanceCap: z
                    .string()
                    .describe(
                      'Maximum number of credits that can roll over to the next cycle when a program renews.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe('Rollover settings.')
                .optional(),
              unitDisplayName: z
                .string()
                .describe('Credit unit display name.')
                .max(32)
                .optional()
                .nullable(),
            })
            .describe(
              "Credit settings.\n\nIf this object is empty, you can't set a price for the benefit."
            )
            .optional(),
          additionalData: z
            .record(z.string(), z.any())
            .describe('Additional information relating to this object.')
            .optional()
            .nullable(),
        })
        .describe('Pool definition benefits and settings.')
        .optional(),
      namespace: z
        .string()
        .describe(
          "Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created."
        )
        .min(1)
        .max(20)
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the pool definition object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
      programDefinitions: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('Program definition ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            externalId: z
              .string()
              .describe('Program definition external ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
          })
        )
        .max(20)
        .optional(),
    })
    .describe('Updated pool definition.')
    .optional(),
});
export const FindPoolDefinitionsByProgramDefinitionRequest = z.object({
  programDefinitionId: z
    .string()
    .describe('ID of the program definition to retrieve its pool definitions.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z.object({
    namespace: z
      .string()
      .describe(
        "Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created."
      )
      .min(1)
      .max(20),
  }),
});
export const FindPoolDefinitionsByProgramDefinitionResponse = z.object({
  poolDefinitions: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Pool definition ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the pool definition is updated.\nTo prevent conflicting changes, the current revision must be specified when updating the pool definition.\n\nIgnored when creating a pool definition.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the pool definition was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the pool definition was updated.')
          .optional()
          .nullable(),
        displayName: z
          .string()
          .describe('Pool definition name.')
          .max(128)
          .optional(),
        programDefinitionIds: z.array(z.string()).max(20).optional(),
        details: z
          .object({
            benefits: z
              .array(
                z.object({
                  benefitKey: z
                    .string()
                    .describe(
                      'Unique identifier for this benefit.\n\nThis key is consistent across the pool definition and all associated pools that contain this benefit.'
                    )
                    .max(64)
                    .optional(),
                  itemSetId: z
                    .string()
                    .describe(
                      'ID that is used to associated items with this benefit.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  price: z
                    .string()
                    .describe(
                      "Price of the benefit in credits. The price is the same for all of this benefit's items."
                    )
                    .optional()
                    .nullable(),
                  additionalData: z
                    .record(z.string(), z.any())
                    .describe('Additional information for this benefit.')
                    .optional()
                    .nullable(),
                  providerAppId: z
                    .string()
                    .describe('ID of the app providing the benefit items.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  displayName: z
                    .string()
                    .describe('Benefit name.')
                    .max(40)
                    .optional()
                    .nullable(),
                  description: z
                    .string()
                    .describe('Benefit description.')
                    .max(255)
                    .optional()
                    .nullable(),
                })
              )
              .max(10)
              .optional(),
            creditConfiguration: z
              .object({
                amount: z
                  .string()
                  .describe('Initial available amount for associated balances.')
                  .optional(),
                rolloverConfiguration: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        'Whether unused credits roll over to a new cycle when a program renews.'
                      )
                      .optional()
                      .nullable(),
                    balanceCap: z
                      .string()
                      .describe(
                        'Maximum number of credits that can roll over to the next cycle when a program renews.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe('Rollover settings.')
                  .optional(),
                unitDisplayName: z
                  .string()
                  .describe('Credit unit display name.')
                  .max(32)
                  .optional()
                  .nullable(),
              })
              .describe(
                "Credit settings.\n\nIf this object is empty, you can't set a price for the benefit."
              )
              .optional(),
            additionalData: z
              .record(z.string(), z.any())
              .describe('Additional information relating to this object.')
              .optional()
              .nullable(),
          })
          .describe('Pool definition benefits and settings.')
          .optional(),
        namespace: z
          .string()
          .describe(
            "Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created."
          )
          .min(1)
          .max(20)
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for the pool definition object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        programDefinitions: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Program definition ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              externalId: z
                .string()
                .describe('Program definition external ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            })
          )
          .max(20)
          .optional(),
      })
    )
    .optional(),
});
